import React, { ReactNode } from 'react';
import { Asset } from 'contentful';
import {
  IButtonFields,
  IFooter,
  IIcon,
  IImageLink,
  ILink,
  ILinkGroup,
  INavigationBar,
  IStaticLink,
} from '../../../types/@types/generated/contentful';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import {
  NavLinkItemType,
  NavItemType,
  ServerNavLinkItemType,
  ServerNavItemType,
  FooterPagesItemType,
} from '../../component-library/navigation/navTypes';
import getLinkHref from './getLinkHref';
import { getCategoryUrl } from '../../component-library/navigation/utils';
import Image from '../Image';
import LazyLoadStyle from '../../component-library/LazyLoadStyle';

export type NavBarButtonType = {
  prompt?: string;
  colorContext: 'onYellow' | 'onDark' | 'onLight';
  label: string;
  variant: 'solid' | 'outline';
  href?: string;
  size: 'small' | 'large';
  trailingIconName?: 'arrow-right';
  leadingIconName?: 'arrow-left';
};

function processNavigationButton(buttonFields?: IButtonFields): NavBarButtonType | null {
  if (!buttonFields) return null;
  const {
    prompt,
    colorContext,
    label,
    variant,
    href,
    size,
    trailingIconName,
    leadingIconName,
  } = buttonFields;
  return {
    prompt,
    colorContext,
    label,
    variant,
    href,
    size,
    trailingIconName,
    leadingIconName,
  };
}

export type NavBarServerProps = {
  imagePortrait?: Asset;
  navButton?: NavBarButtonType | null;
  mobileBottomLinks?: ServerNavLinkItemType[];
  linkGroups?: ServerNavLinkItemType[];
};

export function processNavigationBarData(navigationBar?: INavigationBar): NavBarServerProps {
  if (navigationBar) {
    return {
      imagePortrait: navigationBar.fields?.logo?.fields?.imagePortrait,
      navButton: processNavigationButton(navigationBar.fields?.headerButton?.fields),
      mobileBottomLinks: processServerLinkGroups(navigationBar.fields?.mobileBottomLinks),
      linkGroups: processServerLinkGroups(navigationBar.fields?.linkGroups),
    };
  }
  return {};
}

export type FooterBarServerProps = {
  imageLandscape?: Asset;
  imagePortrait?: Asset;
  linkGroups?: ServerNavLinkItemType[];
  storeGroups?: ServerNavLinkItemType[];
  socialMedia?: ServerNavItemType[];
  paymentProviders?: Asset[];
  bottomLinks?: ServerNavItemType[];
  disclaimer?: string;
  copyright?: string;
};

export function processFooterBarData(footerBar?: IFooter): FooterBarServerProps {
  if (footerBar) {
    return {
      imageLandscape: footerBar.fields?.logo?.fields?.imageLandscape,
      imagePortrait: footerBar.fields?.logo?.fields?.imagePortrait,
      linkGroups: processServerLinkGroups(footerBar?.fields?.linkGroups),
      storeGroups: processServerLinkGroups(footerBar?.fields?.storeGroups),
      socialMedia: processServerImageLinks(footerBar?.fields?.socialMedia),
      bottomLinks: processServerLinks(footerBar?.fields?.bottomLinks),
      disclaimer: footerBar?.fields?.disclaimer,
      copyright: footerBar?.fields?.copyright,
      paymentProviders: footerBar?.fields?.paymentProviders?.map(
        (icon: IIcon) => icon?.fields.icon,
      ),
    };
  }
  return {};
}

export function processServerLinkGroups(
  linkGroups?: ILinkGroup[],
): ServerNavLinkItemType[] | undefined {
  return linkGroups?.map(({ fields, sys }) => ({
    links: processServerLinks(fields.links) ?? [],
    titleLink: getLinkHref(fields.titleLink as ILink),
    title: fields.title,
    id: sys.id,
  }));
}

export function processLinkGroups(
  linkGroups: ServerNavLinkItemType[] | undefined,
): NavLinkItemType[] {
  return (
    linkGroups?.map(({ links, titleLink, title, id }) => ({
      links: processLinks(links ?? []),
      titleLink: titleLink,
      title: title,
      id: id,
    })) ?? []
  );
}

export function processServerImageLinks(
  imageLinks?: IImageLink[],
): ServerNavItemType[] | undefined {
  return imageLinks?.map((imageLink: IImageLink) => {
    const href = imageLink.fields?.pageLink
      ? getCategoryUrl({
          name: imageLink.fields.pageLink.fields.slug,
          tags: imageLink.fields.pageLink.fields.tags,
        })
      : imageLink.fields?.link;
    const image = imageLink.fields?.image;
    return {
      href,
      title: imageLink.fields.title,
      content: imageLink.fields.title,
      image,
      richText: imageLink.fields?.richText && documentToPlainTextString(imageLink.fields.richText),
      description:
        imageLink.fields?.description && documentToPlainTextString(imageLink.fields.description),
    };
  });
}

export function processImageLinks(imageLinks?: ServerNavItemType[]): NavItemType[] | undefined {
  return imageLinks?.map((imageLink: ServerNavItemType) => {
    const image = (
      <LazyLoadStyle>
        <Image asset={imageLink?.image} />
      </LazyLoadStyle>
    );
    return {
      href: imageLink.href,
      title: imageLink.title,
      content: imageLink.title,
      image,
      richText: imageLink.richText,
      description: imageLink.description,
    };
  });
}

export function processAssets(assets?: Asset[]): ReactNode[] | undefined {
  return assets?.map((asset: Asset, index) => (
    <LazyLoadStyle key={index}>
      <Image asset={asset} />
    </LazyLoadStyle>
  ));
}

export function processServerLinks(
  links: (IImageLink | ILink)[] | undefined,
): ServerNavItemType[] | undefined {
  return links?.map((link) => {
    if ('identifier' in link.fields) {
      return {
        title: link.fields?.identifier,
        content: link.fields?.identifier,
        href: getLinkHref(link as ILink),
      };
    } else {
      const href = link.fields?.pageLink
        ? getCategoryUrl({
            name: link.fields.pageLink.fields.slug,
            tags: link.fields.pageLink.fields.tags,
          })
        : link.fields?.link;
      const image = link.fields?.image;
      return {
        title: link.fields?.title,
        content: link.fields?.title,
        href,
        image,
        richText: link.fields?.richText && documentToPlainTextString(link.fields.richText),
        description: link.fields?.description && documentToPlainTextString(link.fields.description),
      };
    }
  });
}

export function processLinks(links: ServerNavItemType[]): NavItemType[] | undefined {
  return links?.map((link) => {
    if (link.image) {
      const image = (
        <LazyLoadStyle>
          <Image asset={link.image} />
        </LazyLoadStyle>
      );
      return {
        title: link.title,
        content: link.title,
        href: link.href,
        image,
        richText: link.richText,
        description: link.description,
      };
    }
    return {
      title: link.title,
      content: link.content,
      href: link.href,
    };
  });
}

export function processStaticFooter(
  footerPages?: IStaticLink[],
): FooterPagesItemType[] | undefined {
  return footerPages?.map?.((page) => ({
    id: page.sys.id,
    title: page?.fields.title,
    contentTypeId: page?.sys.contentType?.sys.id,
    slug: page.fields.slug,
  }));
}
